import { z } from 'zod';
import { CuidSchema } from './crm.schemas.js';
import {
  MAX_SESSION_MINUTES,
  MIN_SESSION_MINUTES,
  WAR_ROOM_PRIORITIES,
  WAR_ROOM_RULE_KEYS,
} from './warroom.js';

/**
 * مخططات غرفة العمليات.
 */

/**
 * قائمةُ قواعد في سلسلة استعلام: «A,B,C».
 *
 * الاستعلامُ نصٌّ واحد لكلّ مفتاح، والقائمةُ تُكتب مفصولةً بفاصلة وتُقسَّم
 * هنا — فلا يحمل الرابطُ المفتاحَ نفسه مكرَّرًا ولا تُخترع صيغةٌ ثانية.
 */
const RuleKeyList = z.preprocess(
  (v) =>
    typeof v === 'string'
      ? v
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean)
      : v,
  z.array(z.enum(WAR_ROOM_RULE_KEYS)).max(WAR_ROOM_RULE_KEYS.length),
);

export const WarRoomQuerySchema = z.object({
  /** قصر النتائج على قواعد بعينها؛ الغيابُ أو الفراغُ يعني كلَّ المفعَّلة. */
  rules: RuleKeyList.optional(),
  /** إشارات المستخدم الحالي وحده. */
  mine: z.coerce.boolean().optional(),
  /** إظهار المصروف والمؤجَّل أيضًا، للمراجعة. */
  includeDismissed: z.coerce.boolean().optional(),
  /** حد أقصى لكل قاعدة حتى لا تُغرق قاعدة واحدة الشاشة. */
  perRule: z.coerce.number().int().min(1).max(200).default(25),
});
export type WarRoomQuery = z.infer<typeof WarRoomQuerySchema>;

export const UpdateWarRoomRuleSchema = z.object({
  enabled: z.boolean().optional(),
  threshold: z.number().min(0).max(9_999_999).optional(),
  priority: z.enum(WAR_ROOM_PRIORITIES).optional(),
});
export type UpdateWarRoomRuleInput = z.infer<typeof UpdateWarRoomRuleSchema>;

/**
 * صرف الإشارة أو تأجيلها.
 *
 * التأجيل بمدة لا بتاريخ: المستخدم يفكّر بـ"راجعني بعد أسبوع" لا بتاريخ
 * بعينه. صفر يعني صرفًا نهائيًا حتى يتغيّر سبب الإشارة.
 */
export const DismissSignalSchema = z.object({
  signalKey: z.string().trim().min(3).max(120),
  snoozeDays: z.number().int().min(0).max(365).default(0),
  reason: z.string().trim().max(300).optional(),
});
export type DismissSignalInput = z.infer<typeof DismissSignalSchema>;

/**
 * حملة اتصال.
 *
 * تحويل مجموعة إشارات إلى أنشطة موزَّعة على الفريق. هذا هو الفرق بين شاشة
 * تعرض مشكلات وشاشة تُنهيها: بضغطة واحدة تصير الإشارات مهامًا في قوائم
 * أصحابها بمواعيد محددة.
 */
export const CampaignSchema = z.object({
  rule: z.enum(WAR_ROOM_RULE_KEYS),
  /** الإشارات المختارة. فارغة تعني كل إشارات القاعدة الظاهرة. */
  signalKeys: z.array(z.string().trim().min(3).max(120)).max(500).default([]),
  /** المكلَّف؛ فارغ يعني توزيعها على أصحاب السجلات. */
  assigneeId: CuidSchema.nullable().optional(),
  /** موعد التنفيذ بالأيام من اليوم. */
  dueInDays: z.number().int().min(0).max(90).default(0),
  /** عنوان بديل للنشاط. */
  subject: z.string().trim().max(200).optional(),
  note: z.string().trim().max(1000).optional(),
});
export type CampaignInput = z.infer<typeof CampaignSchema>;

/* ==========================================================================
   جلسات التركيز
   ========================================================================== */

/**
 * إنشاء جلسة.
 *
 * المدة تُشتقّ من الطرفين ولا تُرسَل: إرسالها مع `endsAt` يفتح باب تناقضهما،
 * ومن يتناقضان لا يُعرف أيّهما القصد.
 */
export const CreateWarRoomSessionSchema = z
  .object({
    title: z.string().trim().min(2, 'عنوان الجلسة مطلوب.').max(120),
    /**
     * نطاقُ الجلسة: قواعدُ بعينها، والفراغُ كلُّ القواعد المفعَّلة.
     *
     * تُنقّى من التكرار: «A,A» في الطلب تعني A — لا تُرفض ولا تُحسب مرّتين.
     */
    rules: z
      .array(z.enum(WAR_ROOM_RULE_KEYS))
      .max(WAR_ROOM_RULE_KEYS.length)
      .default([])
      .transform((v) => [...new Set(v)]),
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    /**
     * المدعوّون.
     *
     * جلسة بلا مدعوّين ليست جلسة: لا أحد يُشعَر ولا أحد يُنتظر منه شيء.
     */
    participantIds: z
      .array(CuidSchema)
      .min(1, 'اختر من يشارك في الجلسة.')
      .max(50, 'الحد خمسون مشاركًا.'),
  })
  .refine((v) => v.endsAt.getTime() > v.startsAt.getTime(), {
    message: 'نهاية الجلسة يجب أن تكون بعد بدايتها.',
    path: ['endsAt'],
  })
  .refine(
    (v) => (v.endsAt.getTime() - v.startsAt.getTime()) / 60_000 >= MIN_SESSION_MINUTES,
    {
      message: `أقصر جلسة ${MIN_SESSION_MINUTES} دقيقة.`,
      path: ['endsAt'],
    },
  )
  .refine(
    (v) => (v.endsAt.getTime() - v.startsAt.getTime()) / 60_000 <= MAX_SESSION_MINUTES,
    {
      message: 'الجلسة أطول من ثماني ساعات — قسّمها.',
      path: ['endsAt'],
    },
  );
export type CreateWarRoomSessionInput = z.infer<typeof CreateWarRoomSessionSchema>;

export const WarRoomSessionListQuerySchema = z.object({
  /** الجارية والمجدولة فقط — وهو ما تعرضه الشاشة افتراضيًا. */
  openOnly: z.coerce.boolean().optional(),
});
export type WarRoomSessionListQuery = z.infer<typeof WarRoomSessionListQuerySchema>;
